import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Loader2 } from "lucide-react";
import { Header } from "./Header";
import { ContactAndFooter } from "./ContactAndFooter";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  {
    title: "Personuppgiftsansvarig",
    text: "Studentflyttarna, som drivs av Josef Muhammed i Umeå, är personuppgiftsansvarig för de uppgifter du lämnar till oss. Du når oss på kontakt@studentflyttarna.se eller 070-727 29 41.",
  },
  {
    title: "Vilka uppgifter samlar vi in?",
    text: "När du skickar en offertförfrågan sparar vi ditt namn, din e-postadress, ditt telefonnummer, från- och till-adress, önskat flyttdatum, bohagsstorlek samt eventuellt val av presentkort. Vi samlar aldrig in fler uppgifter än nödvändigt.",
  },
  {
    title: "Varför behandlar vi uppgifterna?",
    text: "Vi använder uppgifterna för att kunna återkomma med offert, planera och genomföra din flytt samt skicka bekräftelser och påminnelser via e-post. Den rättsliga grunden är fullgörande av avtal och vårt berättigade intresse av att hantera din förfrågan.",
  },
  {
    title: "Hur länge sparas uppgifterna?",
    text: "Uppgifterna sparas bara så länge som behövs för att hantera din förfrågan, genomföra flytten och uppfylla bokföringskrav. Därefter raderas eller avidentifieras de.",
  },
  {
    title: "Dela vi uppgifter med andra?",
    text: "Vid utplacering kan offert- och fakturaunderlag delas med ditt universitet eller din region enligt bidragsbeslutet. Vi säljer aldrig dina uppgifter vidare till tredje part.",
  },
  {
    title: "Dina rättigheter",
    text: "Du har rätt att få tillgång till, rätta eller få dina uppgifter raderade, samt att begränsa behandlingen. Använd formuläret för raderingsbegäran längre ner på sidan eller maila kontakt@studentflyttarna.se. Du kan även lämna klagomål till Integritetsskyddsmyndigheten (IMY).",
  },
  {
    title: "Besöksstatistik (valfritt)",
    text: "Om du godkänner det i samtyckesbannern räknar vi anonyma sidvisningar för att förstå hur sidan används. Det sker utan IP-adress, utan cookies och utan personuppgifter.",
  },
  {
    title: "Cookies",
    text: "Den här webbplatsen använder inga spårningscookies. Läs mer om exakt vad som lagras i vår cookiepolicy.",
  },
];

const darkInput =
  "w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#16a34a] focus:ring-2 focus:ring-[#16a34a]/20 transition-[border-color,box-shadow] duration-300";

export default function PrivacyPolicy() {
  const [del, setDel] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const submitDeletion = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/deletion-requests`, del);
      toast.success("Tack! Din raderingsbegäran är skickad – vi bekräftar via e-post.");
      setDel({ name: "", email: "", message: "" });
    } catch {
      toast.error("Något gick fel. Maila oss på kontakt@studentflyttarna.se istället.");
    }
    setSending(false);
  };

  return (
    <div data-testid="privacy-page" className="bg-[#fafafa] min-h-screen">
      <Header />
      <section className="relative bg-[#090d16] grain pt-40 pb-20 overflow-hidden">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 text-center sm:text-left">
          <p className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-green-400 mb-4">Juridik</p>
          <h1 data-testid="privacy-heading" className="font-display font-extrabold text-white text-4xl sm:text-6xl tracking-tight">
            Integritetspolicy
          </h1>
          <p className="mt-4 text-slate-400 text-sm">Senast uppdaterad: september 2026</p>
        </div>
      </section>
      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 flex flex-col gap-10">
          {SECTIONS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.05}>
              <div data-testid={`privacy-section-${i}`} className="border-b border-slate-200 pb-8">
                <h2 className="font-display font-bold text-xl sm:text-2xl text-[#0b0f19]">{s.title}</h2>
                <p className="mt-3 text-sm sm:text-base leading-relaxed text-slate-600">{s.text}</p>
              </div>
            </Reveal>
          ))}

          <Reveal delay={0.1}>
            <div data-testid="deletion-request" className="rounded-2xl bg-[#0b0f19] p-7 sm:p-9 grain relative overflow-hidden">
              <div className="flex items-center gap-3 mb-3">
                <Trash2 className="w-6 h-6 text-green-400" />
                <h2 className="font-display font-bold text-xl sm:text-2xl text-white">Begär radering av dina uppgifter</h2>
              </div>
              <p className="text-sm sm:text-base text-slate-400 leading-relaxed">
                Fyll i formuläret så raderar vi alla uppgifter vi har om dig och bekräftar via e-post inom 30 dagar.
              </p>
              <form data-testid="deletion-form" onSubmit={submitDeletion} className="mt-6 grid sm:grid-cols-2 gap-4">
                <input
                  data-testid="deletion-name"
                  required
                  value={del.name}
                  onChange={(e) => setDel({ ...del, name: e.target.value })}
                  placeholder="Namn"
                  className={darkInput}
                />
                <input
                  data-testid="deletion-email"
                  required
                  type="email"
                  value={del.email}
                  onChange={(e) => setDel({ ...del, email: e.target.value })}
                  placeholder="E-postadressen du angett hos oss"
                  className={darkInput}
                />
                <textarea
                  data-testid="deletion-message"
                  value={del.message}
                  onChange={(e) => setDel({ ...del, message: e.target.value })}
                  placeholder="Meddelande (frivilligt)"
                  rows={2}
                  className={`${darkInput} resize-none sm:col-span-2`}
                />
                <button
                  data-testid="deletion-submit"
                  type="submit"
                  disabled={sending}
                  className="sm:col-span-2 w-fit bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white font-semibold px-6 py-3.5 rounded-full flex items-center gap-2 transition-colors duration-300"
                >
                  {sending ? (
                    <>Skickar <Loader2 className="w-4 h-4 animate-spin" /></>
                  ) : (
                    "Skicka raderingsbegäran"
                  )}
                </button>
              </form>
            </div>
          </Reveal>
        </div>
      </section>
      <ContactAndFooter />
    </div>
  );
}
